import React, { useCallback, useRef, useState, useEffect } from "react";
import { animated, useSpring } from "react-spring";
import { DA_ConfirmModal } from "@danishagro/shared/src/components/molecules/ConfirmModal/ConfirmModal.component";
import { getB2bImage } from "@danishagro/shared/src/helpers/getB2bImage.helper";
import { ModalSize, useModal } from "@danishagro/shared/src/contexts/modal.context";
import { useDebounce, useMeasure } from "react-use";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_ButtonColor } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_BasicLink } from "@danishagro/shared/src/components/atoms/BasicLink/BasicLink.component";
import { DA_Image } from "@danishagro/shared/src/components/atoms/Image/Image.component";
import { useProductTracker } from "@danishagro/shared/src/gtm/useProductsTracker";
import { B2bImageSrc } from "@danishagro/shared/src/helpers/imageSrc.helper";
import { ImageConfig } from "@danishagro/shared/src/content/imageConfigs/imageConfig.enum";
import { useBasket } from "@danishagro/shared/src/contexts/basket/basket.context";
import {
    DA_BasketLinePrice,
    DA_BasketOrderLine,
} from "@danishagro/shared/src/interfaces/basket.interface";
import { usePrices } from "@danishagro/shared/src/hooks/usePrices.hook";
import { DA_CartItemDesktop } from "./ScreenSizes/CartItemDesktop/CartItemDesktop.component";
import { DA_CartItemMobile } from "./ScreenSizes/CartItemMobile/CartItemMobile.component";
import { DA_CartItemDrawer } from "./CartItemDrawer/CartItemDrawer.component";
// eslint-disable-next-line css-modules/no-unused-class
import S from "./CartItem.module.scss";

export interface _CartItemProps extends DA_BasketOrderLine {
    linePrice?: DA_BasketLinePrice;
    dimmed?: boolean;
    readOnly?: boolean;
    hideDeliveryOptions?: boolean;
    showDeliveryText?: boolean;
    extraPadding?: boolean;
    showAlternativeCardItem?: boolean;
    hideDriverMessageButton?: boolean;
    farmInTimeReferenceId?: string;
    isReceiptPage?: boolean;
    showCheckboxes?: boolean;
    checked?: boolean;
    disableDeleteButtons?: boolean;
    onCheckboxChange?: () => void;
}

export const _CartItem = React.memo(
    ({
        dimmed,
        readOnly,
        hideDeliveryOptions,
        // showDeliveryText,
        linePrice,
        hideDriverMessageButton,
        farmInTimeReferenceId,
        showAlternativeCardItem = false,
        isReceiptPage,
        // showCheckboxes,
        // checked,
        disableDeleteButtons,
        // onCheckboxChange,
        ...data
    }: _CartItemProps) => {
        const { isMobile } = useScreen();

        const {
            // id,
            productId,
            productVariantId,
            productNumber,
            productName,
            driverMessage,
            image,
            quantity: defaultQuantity,
            unit,
        } = data;

        //recaptcha

        const [squashed, setSquashed] = useState(false);
        const [isUpdating, setIsUpdating] = useState(false);
        const [isRemoving, setIsRemoving] = useState(false);
        const [quantity, setQuantity] = useState(defaultQuantity);
        const [localQuantity, setLocalQuantity] = useState(quantity);

        const { getDictionaryString } = useTranslations();
        const { currentCulture } = useAppData();
        const { formatPrice } = usePrices();

        const overlayKey = useRef(0);

        const { isUpdatingBasket, updateBasket, removeFromBasket, priceData } = useBasket();

        const { showModal } = useModal();
        const { showOverlay, closeOverlay } = useDynamicOverlay();
        const [itemRef, { height }] = useMeasure();
        const controllerRef = useRef<AbortController | null>();
        const [overlayContent, setOverlayContent] = useState(null);

        const [overlayIsOpen, setOverlayIsOpen] = useState(false);

        const url = `/${data.url}`.replace(/^\/+/, "/");
        const sizedImage = getB2bImage(image, 85);

        const styles = useSpring({
            height: squashed ? 0 : height,
            immediate: !squashed,
        });

        useEffect(() => {
            setQuantity(defaultQuantity);
        }, [defaultQuantity]);

        const getCategoryFromUrl = (url: string = "") => {
            if (!url) return "";
            const segments = url?.split("/").filter(Boolean); // removes leading/trailing empty
            return segments.slice(0, -1).join("_"); // remove last (product), join with _
        };

        const getListNameFromUrl = (url: string = "") => {
            if (!url) return "";
            const segments = url?.split("/").filter(Boolean);
            const lastCategory = segments.length > 1 ? segments[segments.length - 2] : "";
            return capitalizeFirst(lastCategory);
        };

        const capitalizeFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

        const category = getCategoryFromUrl(data?.url);
        const itemListName = getListNameFromUrl(data?.url);

        // // GTM Tracking product
        const trackProductEvent = useProductTracker({
            name: data?.productName,
            price: formatPrice(Number(data?.price?.totalPrice)),
            productNumber: data?.productNumber,
            currency: "DKK",
            quantity: data?.quantity,
            category,
            listName: itemListName,
        });

        const updateQuantity = useCallback(
            (newQuantity: number) => {
                setQuantity(newQuantity);
                if (!isUpdatingBasket) {
                    setIsUpdating(true);
                }
            },
            [isUpdatingBasket]
        );

        const updateQuantityDesktop = useCallback(
            (newQuantity: number) => {
                setQuantity(newQuantity);

                if (!isUpdatingBasket) {
                    setIsUpdating(true);
                }
            },
            [isUpdatingBasket]
        );

        useDebounce(
            () => {
                if (quantity !== defaultQuantity) {
                    if (controllerRef.current) {
                        // If a change has already been sent to the API,
                        // abort it before sending a new one
                        controllerRef.current.abort();
                    }

                    controllerRef.current = new AbortController();

                    setIsUpdating(true);
                    updateBasket(
                        {
                            ...data,
                            quantity,
                        },
                        { signal: controllerRef.current.signal }
                    )
                        .catch(() => {
                            // Reset item
                            console.error("Error updating cart");
                            setQuantity(defaultQuantity);
                        })
                        .finally(() => setIsUpdating(false));
                } else {
                    setIsUpdating(false);
                }
            },
            500,
            [quantity]
        );

        const handleCloseOverlay = useCallback(() => {
            closeOverlay();
            setOverlayIsOpen(false);
        }, [closeOverlay]);

        const remove = useCallback(() => {
            setIsRemoving(true);
            // GTM Tracking
            trackProductEvent("remove_from_cart");
            removeFromBasket(data)
                .then(() => {
                    // Remove item
                    if (isMobile) {
                        closeOverlay();
                        setOverlayIsOpen(false);
                    }
                    setSquashed(true);
                })
                .finally(() => setIsRemoving(false));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [data, closeOverlay]);

        const confirmRemove = useCallback(
            () =>
                showModal(
                    <DA_ConfirmModal
                        title={getDictionaryString("remove product")}
                        description={getDictionaryString("confirm removal of product")}
                        approveButtonLabel={getDictionaryString("remove")}
                        approveButtonColor={DA_ButtonColor.Alert}
                        onApprove={() => remove()}
                        denyButtonLabel={getDictionaryString("cancel")}
                    />,
                    { size: ModalSize.XS }
                ),
            [showModal, getDictionaryString, remove]
        );

        // Drawer for Mobile
        useEffect(() => {
            const overlayContent = (
                <DA_CartItemDrawer
                    key={`cart-item-drawer-${data.id}`}
                    productId={productId}
                    productVariantId={productVariantId}
                    url={url}
                    sizedImageMobile={sizedImage}
                    productNumber={productNumber}
                    productName={productName}
                    priceData={priceData}
                    readOnly={readOnly}
                    updateQuantity={updateQuantity}
                    localQuantity={localQuantity}
                    dimmed={dimmed || isUpdatingBasket}
                    quantity={quantity}
                    unit={unit}
                    currentCulture={currentCulture}
                    confirmRemove={confirmRemove}
                    farmInTimeReferenceId={farmInTimeReferenceId}
                    isReceiptPage={isReceiptPage}
                    imageUrl={data.imageUrl}
                    data={data}
                    hideDeliveryOptions={hideDeliveryOptions}
                    linePrice={linePrice}
                    overlayIsOpen={overlayIsOpen}
                    closeOverlay={handleCloseOverlay}
                />
            );

            setOverlayContent(overlayContent);
            // If the overlay is currently open update the content
            if (overlayIsOpen) {
                closeOverlay();
                showOverlay({
                    id: `checkout-product-details-overlay-updated-${overlayKey.current++}`,
                    title: getDictionaryString("ProductDetails"),
                    content: overlayContent,
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [readOnly, unit, quantity, overlayIsOpen, priceData]);

        // Function to open the overlay
        const openProductDrawer = useCallback(() => {
            showOverlay({
                id: "checkout-product-details-overlay",
                title: getDictionaryString("ProductDetails"),
                content: overlayContent,
            });

            setOverlayIsOpen(true);
            setLocalQuantity(quantity);

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [getDictionaryString, overlayContent, quantity, showOverlay]);

        return (
            <animated.div style={styles}>
                <div className={S.wrapper} ref={itemRef}>
                    <div className={cn(S.item, showAlternativeCardItem && S.mainInfoAltItem)}>
                        {isMobile ? (
                            showAlternativeCardItem ? (
                                <div className={S.mainInfoAlt}>
                                    <div className={S.imageWrapper}>
                                        <DA_BasicLink href={url} className={S.imageLink}>
                                            <DA_Image
                                                src={B2bImageSrc(
                                                    sizedImage.url,
                                                    ImageConfig.PdpThumbnail
                                                )}
                                                alt={sizedImage.alt}
                                                width={sizedImage.width}
                                                height={sizedImage.height}
                                                className={S.image}
                                            />
                                        </DA_BasicLink>
                                    </div>
                                    <div className={S.mainInfoAltContent}>
                                        {/** Name */}
                                        <div className={S.numberAndName}>
                                            <div className={S.name}>
                                                <DA_BasicLink href={url} className={S.nameLink}>
                                                    {productName}
                                                </DA_BasicLink>
                                            </div>
                                        </div>

                                        <div className={S.totalsWrapper}>
                                            {/** Amount */}
                                            <div className={S.actions}>
                                                <div className={S.amountInfo}>
                                                    {quantity} {unit}
                                                </div>
                                            </div>

                                            {/** Price */}
                                            <div className={S.total}>
                                                {/* {priceData.lines} */}
                                                XXXXX.XX
                                                {/* {priceData.charges.length > 0 && (
                                                    <div className={S.charges}>
                                                        {priceData.charges.map((fee) => (
                                                            <div key={fee.code}>
                                                                {fee.title}: {fee.price}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )} */}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <DA_CartItemMobile
                                    id={data.id}
                                    url={url}
                                    imageUrl={data.imageUrl}
                                    sizedImage={sizedImage}
                                    productNumber={productNumber}
                                    productName={productName}
                                    productId={productId}
                                    productVariantId={productVariantId}
                                    quantity={quantity}
                                    unit={unit}
                                    driverMessage={driverMessage}
                                    readOnly={readOnly}
                                    dimmed={dimmed}
                                    isUpdating={isUpdating}
                                    isRemoving={isRemoving}
                                    confirmRemove={confirmRemove}
                                    linePrice={linePrice}
                                    priceData={priceData}
                                    data={data}
                                    hideDeliveryOptions={hideDeliveryOptions}
                                    hideDriverMessageButton={hideDriverMessageButton}
                                    farmInTimeReferenceId={farmInTimeReferenceId}
                                    updateQuantityDesktop={updateQuantity}
                                    disableDeleteButton={disableDeleteButtons}
                                    openProductDrawer={openProductDrawer}
                                />
                            )
                        ) : (
                            <DA_CartItemDesktop
                                id={data.id}
                                url={url}
                                imageUrl={data.imageUrl}
                                sizedImage={sizedImage}
                                productNumber={productNumber}
                                productName={productName}
                                productId={productId}
                                productVariantId={productVariantId}
                                quantity={quantity}
                                unit={unit}
                                driverMessage={driverMessage}
                                readOnly={readOnly}
                                dimmed={dimmed}
                                isUpdating={isUpdating}
                                isRemoving={isRemoving}
                                confirmRemove={confirmRemove}
                                linePrice={linePrice}
                                priceData={priceData}
                                data={data}
                                hideDeliveryOptions={hideDeliveryOptions}
                                hideDriverMessageButton={hideDriverMessageButton}
                                farmInTimeReferenceId={farmInTimeReferenceId}
                                updateQuantityDesktop={updateQuantityDesktop}
                                disableDeleteButton={disableDeleteButtons}
                            />
                        )}
                    </div>
                </div>
            </animated.div>
        );
    }
);

_CartItem.displayName = "CartItem";
